import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLayout } from "../context/LayoutContext";
import { useChatContext } from "./ChatContext";
import { useWorkflowLimits } from "../components/useWorkflowLimits";
import { useBluelagoon } from "@bluelagoon";
import { track } from "@crm/analytics";
import Header from "../components/header";
import ChatMainInput from "./components/ChatMainInput";
import WorkflowLimitBanner from "./components/WorkflowLimitBanner";
import RecommendedBlueprints from "./components/RecommendedBlueprints";
import ModeToggle from "./components/ModeToggle";
import RecentProjectsGrid from "./components/RecentProjectsGrid";

const USER_TYPE_STORAGE_KEY = "agentui_user_type";

export default function ChatPage() {
    const { t } = useTranslation();
    const { isSuperAdmin, user } = useAuth();
    const { toggleSidebar, sidebarOpen } = useLayout();
    const { setAppViewMode } = useChatContext();
    const [searchParams] = useSearchParams();
    const [userType, setUserType] = useState(null);
    const [initialMessage, setInitialMessage] = useState("");
    const chatInputRef = useRef(null);
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState([]);
    const [loadingWorkflows, setLoadingWorkflows] = useState(true);
    const [buildMode, setBuildMode] = useState("apps");
    const isAutomationMode = buildMode === "automations";

    const handleModeChange = useCallback((newMode) => {
        setBuildMode(newMode);
        track("Build Mode Changed", { mode: newMode });
    }, []);

    useEffect(() => {
        setAppViewMode("chat");
    }, [setAppViewMode]);

    useEffect(() => {
        const urlUserType = searchParams.get("user_type");
        if (urlUserType) {
            setUserType(urlUserType);
            localStorage.setItem(USER_TYPE_STORAGE_KEY, urlUserType);
        } else {
            setUserType(localStorage.getItem(USER_TYPE_STORAGE_KEY) || null);
        }
    }, [searchParams]);

    const { activeWorkflowCount, workflowLimit, currentPlan, isAtLimit, loading: limitsLoading } =
        useWorkflowLimits();

    const { call } = useBluelagoon({ method: "recentWorkflows" });

    const handlePromptClick = (prompt) => {
        setInitialMessage(prompt);
        track("Example Selected", { prompt: prompt.substring(0, 100), userType: userType || "none" });
        setTimeout(() => chatInputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    };

    useEffect(() => {
        const loadWorkflows = async () => {
            try {
                setLoadingWorkflows(true);
                const response = await call({ limit: 6 });
                if (response?.workflows) setWorkflows(response.workflows);
            } catch (err) {
                console.error("Error loading workflows:", err);
            } finally {
                setLoadingWorkflows(false);
            }
        };
        loadWorkflows();
    }, [call]);

    const skeletons = Array.from({ length: 6 }).map((_, i) => (
        <div key={`sk-${i}`} className="border border-gray-200 rounded-lg p-4 animate-pulse">
            <div className="bg-gray-200 rounded-md aspect-video mb-3" />
            <div className="bg-gray-200 h-4 w-3/4 rounded" />
        </div>
    ));

    return (
        <>
            <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} noMargin />
            <div className="min-h-[calc(100vh-82px)] bg-white flex flex-col items-center p-4 pt-16">
                <div className="w-full max-w-3xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                            {user?.firstName
                                ? t("chatPage.title", { firstName: user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1) })
                                : t("chatPage.titleFallback")}
                        </h1>
                        <p className="text-lg text-gray-600">
                            {t(isAutomationMode ? "chatPage.modeToggle.subtitleAutomations" : "chatPage.subtitle")}
                        </p>
                        <div className="mt-4 flex justify-center">
                            <ModeToggle mode={buildMode} onModeChange={handleModeChange} />
                        </div>
                    </div>

                    {!isSuperAdmin && isAtLimit && !limitsLoading && (
                        <WorkflowLimitBanner workflowLimit={workflowLimit} activeWorkflowCount={activeWorkflowCount} currentPlan={currentPlan} />
                    )}

                    <div ref={chatInputRef}>
                        <ChatMainInput
                            disabled={!isSuperAdmin && isAtLimit}
                            initialMessage={initialMessage}
                            onMessageSet={() => setInitialMessage("")}
                            minHeight="30px"
                            placeholderOverride={isAutomationMode ? t("chatPage.modeToggle.placeholderAutomations") : null}
                            messagePrefix={isAutomationMode ? t("chatPage.modeToggle.automationPrompt") : null}
                        />
                    </div>

                    <div className="mt-8 w-full">
                        {loadingWorkflows ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">{skeletons}</div>
                        ) : workflows.length > 0 ? (
                            <RecentProjectsGrid workflows={workflows} navigate={navigate} t={t} />
                        ) : (
                            <RecommendedBlueprints userType={userType} onPromptClick={handlePromptClick} user={user} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
